import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import axios, { apiBaseUrl } from '../api/httpClient';
import { useAuth } from '../auth/AuthContext';
import { imsBasePath, imsPath, imsSections, resolveImsLocation } from './integratedManagementNavigation';
import { ImsOverview, imsTimestamp } from './integratedManagementTypes';
import IntegratedManagementIcon from './IntegratedManagementIcon';
import IntegratedManagementOperationalRecords from './IntegratedManagementOperationalRecords';
import IntegratedManagementWorkflows from './IntegratedManagementWorkflows';
import IntegratedManagementAutomation from './IntegratedManagementAutomation';
import IntegratedManagementSupportSlas from './IntegratedManagementSupportSlas';
import { ImsDashboard, ImsProgrammeActions, ImsStandards, ImsDataRoom, ImsReports, ImsArchive } from './IntegratedManagementWorkspaceViews';
import './IntegratedManagementSystemPage.css';

const IntegratedManagementSystemPage: React.FC = () => {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const current = resolveImsLocation(pathname);
  const { user, hasPermission } = useAuth();
  const [overview, setOverview] = useState<ImsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const sequence = useRef({ value: 0 });
  const main = useRef<HTMLElement | null>(null);
  const previousPath = useRef(pathname);
  const canManage = hasPermission('integrated-management-system.manage');
  const groups = Array.from(new Set(imsSections.map((section) => section.group)));
  const load = useCallback(async () => {
    const request = ++sequence.current.value;
    setLoading(true); setError('');
    try {
      const response = await axios.get<ImsOverview>(`${apiBaseUrl}/integrated-management-system`);
      if (request === sequence.current.value) setOverview(response.data);
    } catch {
      if (request === sequence.current.value) setError('The management-system overview could not be loaded. Refresh to try again; unavailable information is not a pass.');
    } finally {
      if (request === sequence.current.value) setLoading(false);
    }
  }, []);
  useEffect(() => { const counter = sequence.current; load(); return () => { counter.value++; }; }, [load]);
  useEffect(() => {
    setMenuOpen(false);
    if (previousPath.current !== pathname) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      main.current?.focus({ preventScroll: true });
    }
    previousPath.current = pathname;
  }, [pathname]);
  const content = () => {
    if (!overview || !current) return null;
    const { section, tab } = current;
    if (tab.type) return <IntegratedManagementOperationalRecords key={`${section.id}/${tab.id}/${searchParams.get('standard') || ''}`} standards={overview.standards.map((s) => s.code)} recordType={tab.type} title={tab.label} selectedStandard={searchParams.get('standard') || ''} onStandardChange={(standard) => {
      const next = new URLSearchParams(searchParams);
      if (standard) next.set('standard', standard); else next.delete('standard');
      setSearchParams(next);
    }} />;
    switch (tab.view) {
      case 'dashboard': return <ImsDashboard overview={overview} />;
      case 'actions': return <ImsProgrammeActions overview={overview} />;
      case 'checks': return <IntegratedManagementAutomation allowExport={false} />;
      case 'tests': return <IntegratedManagementWorkflows key="tests" workflowKind="control-test" />;
      case 'acknowledgements': return <IntegratedManagementWorkflows key="acknowledgements" workflowKind="policy-acknowledgement" />;
      case 'standards': return <ImsStandards overview={overview} />;
      case 'evidence': return <ImsDataRoom overview={overview} />;
      case 'reports': return <ImsReports overview={overview} />;
      case 'support-slas': return <IntegratedManagementSupportSlas />;
      case 'archive': return <ImsArchive overview={overview} />;
      default: return null;
    }
  };
  return <div className="ims-workspace">
    <a className="ims-skip-link" href="#ims-main">Skip IMS navigation</a>
    <div className="ims-mobile-nav"><span>Gemstone IMS</span><Button variant="outline-secondary" aria-controls="ims-sidebar" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? 'Close navigation' : 'Browse sections'}</Button></div>
    <aside id="ims-sidebar" className={`ims-sidebar${menuOpen ? ' is-open' : ''}`}>
      <Link to={imsBasePath} className="ims-brand"><span className="ims-brand__mark">G</span><span>Gemstone <strong>IMS</strong><small>Integrated management system</small></span></Link>
      <nav aria-label="IMS sections">{groups.map((group) => <div className="ims-nav-group" key={group}>
        <p className="ims-nav-group__label">{group}</p>
        {imsSections.filter((s) => s.group === group).map((section) => <NavLink key={section.id} to={imsPath(section)} end={section.id === 'overview'} className={`ims-nav-link${current?.section.id === section.id ? ' is-active' : ''}`}><IntegratedManagementIcon name={section.icon} /><span>{section.label}</span></NavLink>)}
      </div>)}</nav>
      <div className="ims-sidebar__footer"><span className="ims-avatar" aria-hidden="true">{(user?.name || 'G').split(' ').map((v) => v[0]).slice(0, 2).join('')}</span><div><strong>{user?.name || 'Gemstone staff'}</strong><small>{canManage ? 'Management workspace' : 'Read-only audit workspace'}</small></div></div>
    </aside>
    <main id="ims-main" className="ims-main" tabIndex={-1} ref={main}>
      <header className="ims-workspace-header"><div className="ims-breadcrumb"><Link to={imsBasePath}>IMS</Link><span aria-hidden="true">/</span><span>{current?.section.label || 'Page not found'}</span></div><div className="ims-workspace-header__right"><Badge bg={overview?.migration.cutoverPermitted ? 'success' : 'secondary'}>{overview?.migration.cutoverPermitted ? 'Native Gemstone IMS' : 'Controlled workspace'}</Badge>{!canManage && <Badge bg="secondary">Read only</Badge>}<Button size="sm" variant="outline-secondary" onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh overview'}</Button></div></header>
      {current ? <>
        <div className="ims-page-heading"><p className="ims-eyebrow">{current.section.group}</p><h1>{current.section.label}</h1><p>{current.section.description}</p></div>
        {current.section.tabs.length > 1 && <nav className="ims-section-tabs" aria-label={`${current.section.label} views`}>{current.section.tabs.map((tab) => <NavLink key={tab.id} to={imsPath(current.section, tab)} end className={`ims-section-tab${current.tab.id === tab.id ? ' is-active' : ''}`}>{tab.label}</NavLink>)}</nav>}
        {error && <Alert variant="danger">{error}</Alert>}
        {loading && !overview ? <div className="ims-loading" role="status"><Spinner animation="border" size="sm" /><span>Loading the management workspace…</span></div> : overview && <>
          {overview.migration.cutoverPermitted !== true && <Alert variant="warning"><strong>Parallel run:</strong> Adoptech remains operational until reconciliation, backup/restore, access review and management approval are complete. {overview.migration.cutoverGate}</Alert>}
          <div className="ims-view" key={`${current.section.id}/${current.tab.id}`}>{content()}</div>
          <footer className="ims-view-footer">Four standards. One controlled system.<span>Overview refreshed {imsTimestamp(overview.generatedAt)}</span></footer>
        </>}
      </> : <section className="ims-empty-state"><h1>IMS page not found</h1><p>Choose a section from the navigation, or return to the overview.</p><Link to={imsBasePath} className="btn btn-primary">Back to IMS overview</Link></section>}
    </main>
  </div>;
};
export default IntegratedManagementSystemPage;
